import { useMenu, useTemplates } from '@restaurant/api-client/react';
import { countPlaceholders, type Audience, type CampaignDto, type CampaignInput, type CampaignKind } from '@restaurant/shared';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { Sparkles, Users } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Field, Input } from '@/components/ui/input';
import { Select, Spinner, Tabs, TabsList, TabsTrigger } from '@/components/ui/misc';
import { api } from '@/lib/api';
import { toastError } from '@/lib/errors';
import { formatPhone } from '@/lib/utils';

type Component = { type: string; format?: string; text?: string; buttons?: { type: string; text: string }[] };
const isJpegOrPng = (url: string) => /\.(jpe?g|png)(\?.*)?$/i.test(url);
const numOrNull = (s: string) => (s.trim() === '' ? null : Math.max(1, Math.round(Number(s)) || 1));

const DEFAULT_AUDIENCE: Audience = { orderedItemIds: [], orderedCategoryIds: [], customerIds: [], excludePromotedWithinDays: 3, orderedWithinDays: null, notOrderedWithinDays: null, minOrders: null };

export function CampaignDialog({ campaign, onClose, onDone }: { campaign?: CampaignDto; onClose: () => void; onDone: () => void }) {
  const { data: templates = [] } = useTemplates();
  const { data: menu = [] } = useMenu();
  const [f, setF] = useState<CampaignInput>(() =>
    campaign
      ? {
          name: campaign.name, kind: campaign.kind, templateName: campaign.templateName, language: campaign.language, bodyParams: campaign.bodyParams, headerParams: campaign.headerParams,
          headerImageUrl: campaign.headerImageUrl, orderButtonIndex: campaign.orderButtonIndex, orderItemIds: campaign.orderItemIds, audience: campaign.audience,
        }
      : { name: `Specials ${format(new Date(), 'd MMM')}`, kind: 'promotion', templateName: '', language: 'en_US', bodyParams: [], headerParams: [], headerImageUrl: null, orderButtonIndex: null, orderItemIds: [], audience: DEFAULT_AUDIENCE },
  );
  const [scheduleAt, setScheduleAt] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const a = f.audience;
  const setA = (patch: Partial<Audience>) => setF((p) => ({ ...p, audience: { ...p.audience, ...patch } }));

  const approved = templates.filter((t) => t.status === 'APPROVED');
  const selected = approved.find((t) => t.name === f.templateName && t.language === f.language);
  const comps = (selected?.components ?? []) as Component[];
  const body = comps.find((c) => c.type.toUpperCase() === 'BODY')?.text ?? '';
  const header = comps.find((c) => c.type.toUpperCase() === 'HEADER' && (c.format ?? 'TEXT').toUpperCase() === 'TEXT')?.text ?? '';
  const imageHeader = comps.some((c) => c.type.toUpperCase() === 'HEADER' && c.format?.toUpperCase() === 'IMAGE');
  const quickReplies = (comps.find((c) => c.type.toUpperCase() === 'BUTTONS')?.buttons ?? [])
    .map((b, index) => ({ ...b, index }))
    .filter((b) => b.type.toUpperCase() === 'QUICK_REPLY');
  const photos = useMemo(
    () => menu.flatMap((c) => c.items).filter((i) => i.imageUrl && isJpegOrPng(i.imageUrl)).map((i) => ({ id: i.id, name: i.name, url: i.imageUrl! })),
    [menu],
  );
  const nBody = countPlaceholders(body);
  const nHeader = countPlaceholders(header);

  // Keep the param arrays the same length as the template's {{n}} placeholders.
  useEffect(() => {
    setF((p) => ({
      ...p,
      bodyParams: Array.from({ length: nBody }, (_, i) => p.bodyParams[i] ?? ''),
      headerParams: Array.from({ length: nHeader }, (_, i) => p.headerParams[i] ?? ''),
    }));
  }, [nBody, nHeader, f.templateName, f.language]);

  // Picture only for image-header templates; "Order now" defaults to a quick reply whose label mentions ordering.
  useEffect(() => {
    if (!selected) return;
    setF((p) => ({
      ...p,
      headerImageUrl: imageHeader ? p.headerImageUrl : null,
      orderButtonIndex:
        p.orderButtonIndex !== null && p.orderButtonIndex !== undefined && quickReplies.some((b) => b.index === p.orderButtonIndex)
          ? p.orderButtonIndex
          : (quickReplies.find((b) => /order/i.test(b.text))?.index ?? null),
    }));
  }, [f.templateName, f.language, selected?.id]);

  const [debounced, setDebounced] = useState({ kind: f.kind, audience: a });
  useEffect(() => {
    const t = setTimeout(() => setDebounced({ kind: f.kind, audience: a }), 400);
    return () => clearTimeout(t);
  }, [f.kind, a]);
  const preview = useQuery({ queryKey: ['campaign-preview', debounced], queryFn: () => api.campaigns.preview(debounced.kind, debounced.audience) });

  const itemName = useMemo(() => new Map(menu.flatMap((c) => c.items).map((i) => [i.id, i.name])), [menu]);
  const missing = [...f.bodyParams, ...f.headerParams].some((p) => !p.trim()) || (imageHeader && !f.headerImageUrl);
  const canSave = !!f.name.trim() && !!selected && !missing;

  const suggest = async () => {
    try {
      const s = await api.campaigns.suggestedAudience();
      if (!s.specials) return toast.info('No specials are set for today. Add some on the Menu page first.');
      if (!s.audience.customerIds.length) return toast.info("No past customers match today's specials yet.");
      setA({ customerIds: s.audience.customerIds });
      toast.success(`${s.ordered} ordered these dishes before, ${s.similar} order similar dishes. Customers whose diet doesn't fit are left out.`);
    } catch (err) {
      toastError(err);
    }
  };

  const save = async (): Promise<CampaignDto> => (campaign ? api.campaigns.update(campaign.id, f) : api.campaigns.create(f));

  const run = async (what: 'draft' | 'now' | 'schedule') => {
    if (what === 'now' && !window.confirm(`Send "${f.name}" to ${preview.data?.count ?? '?'} customers now? Meta charges per message.`)) return;
    if (what === 'schedule' && !scheduleAt) return toast.error('Pick a date and time');
    setBusy(what);
    try {
      const saved = await save();
      if (what === 'draft') toast.success('Draft saved');
      else if (what === 'now') {
        await api.campaigns.send(saved.id);
        toast.success('Campaign is sending');
      } else {
        await api.campaigns.send(saved.id, new Date(scheduleAt).toISOString());
        toast.success(`Scheduled for ${format(new Date(scheduleAt), 'd MMM, HH:mm')}`);
      }
      onDone();
    } catch (err) {
      toastError(err);
    } finally {
      setBusy(null);
    }
  };

  const tokenHint = 'Use {name} for the customer name and {specials} for today\'s specials.';

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent title={campaign ? 'Edit campaign' : 'New campaign'} description="Messages are sent with an approved template, paced to stay within your daily limit." side="right">
        <div className="grid gap-4">
          <Field label="Name (internal)">
            <Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} maxLength={100} />
          </Field>
          <Tabs value={f.kind} onValueChange={(k) => setF({ ...f, kind: k as CampaignKind })}>
            <TabsList>
              <TabsTrigger value="promotion">Promotion</TabsTrigger>
              <TabsTrigger value="invite">Opt-in invite</TabsTrigger>
            </TabsList>
          </Tabs>
          <p className="-mt-2 text-xs text-muted-foreground">
            {f.kind === 'promotion'
              ? 'Goes only to customers who agreed to receive promotions.'
              : 'Asks customers who chatted with you (and were never asked) whether they want your specials. A reply of YES opts them in, NO or STOP opts them out. Use a template with a clear question, ideally with "Yes please" / "No thanks" quick-reply buttons.'}
          </p>

          <Field label="Template" hint={approved.length ? undefined : 'No approved templates yet. Create one on the Templates page (category Marketing) and wait for Meta to approve it.'}>
            <Select
              value={selected ? `${selected.name}|${selected.language}` : undefined}
              onValueChange={(v) => {
                const [name, language] = v.split('|');
                setF({ ...f, templateName: name!, language: language! });
              }}
              options={approved.map((t) => ({ value: `${t.name}|${t.language}`, label: `${t.name} (${t.language}, ${t.category.toLowerCase()})` }))}
              placeholder="Pick an approved template"
            />
          </Field>
          {selected ? (
            <div className="rounded-lg bg-muted/60 p-3 text-sm whitespace-pre-wrap">
              {header ? <p className="mb-1 font-semibold">{header}</p> : null}
              {body}
            </div>
          ) : null}
          {imageHeader ? (
            <Field label="Picture" hint="Sent at the top of the message. Pick a menu photo (JPEG/PNG) or upload one.">
              <div className="flex max-h-36 flex-wrap gap-1.5 overflow-y-auto">
                {photos.map((p) => (
                  <button key={p.id} title={p.name} onClick={() => setF({ ...f, headerImageUrl: p.url })} className={`size-14 overflow-hidden rounded-md border-2 ${f.headerImageUrl === p.url ? 'border-primary' : 'border-transparent'}`}>
                    <img src={p.url} alt={p.name} className="size-full object-cover" />
                  </button>
                ))}
                <label className="flex size-14 cursor-pointer items-center justify-center rounded-md border border-dashed text-xs text-muted-foreground hover:bg-accent">
                  Upload
                  <input
                    type="file"
                    accept="image/jpeg,image/png"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      if (!['image/jpeg', 'image/png'].includes(file.type) || file.size > 5 * 1024 * 1024) return void toast.error('Use a JPEG or PNG up to 5 MB');
                      void api.menu.uploadImage(file, file.name).then(({ url }) => setF((p) => ({ ...p, headerImageUrl: url }))).catch(toastError);
                    }}
                  />
                </label>
              </div>
              {f.headerImageUrl && !photos.some((p) => p.url === f.headerImageUrl) ? <img src={f.headerImageUrl} alt="" className="mt-2 max-h-32 rounded-md" /> : null}
            </Field>
          ) : null}
          {quickReplies.length ? (
            <Field label="Order button" hint="When a customer taps it, a draft order is created for staff to confirm, and the customer gets a short reply.">
              <Select
                value={f.orderButtonIndex === null || f.orderButtonIndex === undefined ? 'none' : String(f.orderButtonIndex)}
                onValueChange={(v) => setF({ ...f, orderButtonIndex: v === 'none' ? null : Number(v) })}
                options={[{ value: 'none', label: 'None (buttons just send a reply)' }, ...quickReplies.map((b) => ({ value: String(b.index), label: `"${b.text}" starts an order` }))]}
              />
              {f.orderButtonIndex !== null && f.orderButtonIndex !== undefined ? (
                <div className="mt-2 text-xs">
                  <p className="mb-1 text-muted-foreground">Dishes it orders ({f.orderItemIds.length ? 'picked' : "today's specials"}; with several, the customer picks one):</p>
                  <div className="flex flex-wrap gap-1">
                    {f.orderItemIds.map((id) => (
                      <button key={id} className="rounded-full border px-2 py-0.5" onClick={() => setF({ ...f, orderItemIds: f.orderItemIds.filter((x) => x !== id) })}>{itemName.get(id) ?? 'removed item'} ✕</button>
                    ))}
                    <select
                      className="rounded-full border bg-background px-2 py-0.5"
                      value=""
                      onChange={(e) => e.target.value && setF({ ...f, orderItemIds: [...new Set([...f.orderItemIds, e.target.value])].slice(0, 10) })}
                    >
                      <option value="">+ pick a dish</option>
                      {menu.flatMap((c) => c.items).filter((i) => i.available).map((i) => <option key={i.id} value={i.id}>{i.name}</option>)}
                    </select>
                  </div>
                </div>
              ) : null}
            </Field>
          ) : null}
          {f.headerParams.map((p, i) => (
            <Field key={`h${i}`} label={`Header {{${i + 1}}}`} hint={tokenHint}>
              <Input value={p} onChange={(e) => setF({ ...f, headerParams: f.headerParams.map((x, j) => (j === i ? e.target.value : x)) })} />
            </Field>
          ))}
          {f.bodyParams.map((p, i) => (
            <Field key={`b${i}`} label={`Body {{${i + 1}}}`} hint={i === 0 ? tokenHint : undefined}>
              <Input value={p} onChange={(e) => setF({ ...f, bodyParams: f.bodyParams.map((x, j) => (j === i ? e.target.value : x)) })} placeholder={i === 0 ? '{name}' : '{specials}'} />
            </Field>
          ))}

          <section className="grid gap-3 rounded-xl border p-3">
            <div className="flex items-center justify-between">
              <p className="flex items-center gap-1.5 text-sm font-semibold"><Users className="size-4" /> Audience</p>
              {f.kind === 'promotion' ? <Button size="sm" variant="ghost" onClick={() => void suggest()}><Sparkles /> Fans of today's specials</Button> : null}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Ordered in the last N days">
                <Input inputMode="numeric" value={a.orderedWithinDays ?? ''} onChange={(e) => setA({ orderedWithinDays: numOrNull(e.target.value) })} placeholder="Any time" />
              </Field>
              <Field label="Not ordered for N days" hint="Win back quiet customers">
                <Input inputMode="numeric" value={a.notOrderedWithinDays ?? ''} onChange={(e) => setA({ notOrderedWithinDays: numOrNull(e.target.value) })} placeholder="Off" />
              </Field>
              <Field label="At least N orders">
                <Input inputMode="numeric" value={a.minOrders ?? ''} onChange={(e) => setA({ minOrders: numOrNull(e.target.value) })} placeholder="Any" />
              </Field>
              {f.kind === 'promotion' ? (
                <Field label="Skip if promoted in last N days">
                  <Input inputMode="numeric" value={a.excludePromotedWithinDays} onChange={(e) => setA({ excludePromotedWithinDays: Math.max(0, Math.round(Number(e.target.value)) || 0) })} />
                </Field>
              ) : null}
            </div>
            <Field label="Ordered from these categories">
              <div className="flex flex-wrap gap-1.5">
                {menu.map((c) => {
                  const on = a.orderedCategoryIds.includes(c.id);
                  return (
                    <button
                      key={c.id}
                      onClick={() => setA({ orderedCategoryIds: on ? a.orderedCategoryIds.filter((x) => x !== c.id) : [...a.orderedCategoryIds, c.id] })}
                      className={`rounded-full border px-2 py-0.5 text-xs ${on ? 'border-primary bg-primary/10 text-primary' : 'text-muted-foreground hover:bg-accent'}`}
                    >
                      {c.name}
                    </button>
                  );
                })}
              </div>
            </Field>
            {a.orderedItemIds.length ? (
              <div className="text-xs">
                <span className="text-muted-foreground">Ordered any of: </span>
                {a.orderedItemIds.map((id) => itemName.get(id) ?? 'removed item').join(', ')}{' '}
                <button className="underline" onClick={() => setA({ orderedItemIds: [] })}>clear</button>
              </div>
            ) : null}
            {a.customerIds.length ? (
              <div className="text-xs">
                <span className="text-muted-foreground">Picked for today's specials: </span>
                {a.customerIds.length} customers (by what they and similar customers order, diet-checked){' '}
                <button className="underline" onClick={() => setA({ customerIds: [] })}>clear</button>
              </div>
            ) : null}
            <div className="rounded-lg bg-muted/60 p-2 text-sm">
              {preview.isFetching && !preview.data ? (
                <Spinner />
              ) : preview.data ? (
                <>
                  <p>
                    <b className="tabular-nums">{preview.data.count}</b> customer{preview.data.count === 1 ? '' : 's'} will receive this
                    {preview.data.beforeFrequencyCap > preview.data.count ? ` (${preview.data.beforeFrequencyCap - preview.data.count} skipped: promoted recently)` : ''}.
                    <span className="text-muted-foreground"> {preview.data.remainingToday} of {preview.data.dailyLimit} bulk messages left in the last 24h.</span>
                  </p>
                  {preview.data.count > preview.data.remainingToday ? <p className="text-destructive">Over the daily limit: narrow the audience or send later.</p> : null}
                  {preview.data.sample.length ? (
                    <p className="mt-1 text-xs text-muted-foreground">
                      {preview.data.sample.slice(0, 8).map((s) => s.name ?? formatPhone(s.waId)).join(', ')}
                      {preview.data.count > 8 ? ` and ${preview.data.count - 8} more` : ''}
                    </p>
                  ) : null}
                </>
              ) : null}
            </div>
          </section>

          <div className="grid gap-2">
            <div className="flex flex-wrap gap-2">
              <Button onClick={() => void run('now')} disabled={!canSave || !!busy || !preview.data?.count}>{busy === 'now' ? <Spinner /> : null} Send now</Button>
              <Button variant="outline" onClick={() => void run('draft')} disabled={!canSave || !!busy}>{busy === 'draft' ? <Spinner /> : null} Save draft</Button>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Input type="datetime-local" value={scheduleAt} onChange={(e) => setScheduleAt(e.target.value)} className="w-56" aria-label="Schedule for" />
              <Button variant="outline" onClick={() => void run('schedule')} disabled={!canSave || !!busy}>{busy === 'schedule' ? <Spinner /> : null} Schedule</Button>
            </div>
            {missing ? <p className="text-xs text-muted-foreground">Fill in every template variable.</p> : null}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
